import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { issuerLogoOverride } from '@/components/HomepageV3/DiscoveryTable/Cells/IssuerCell';

import { issuerAvatarRadius } from '@/ui/Avatars/IssuerAvatar';
import { BuildingIcon } from '@/ui/Icons/BuildingIcon';
import { Multiselect, MultiselectItem } from '@/ui/Multiselect';

import { DiscoveryFilters, DiscoveryIssuerResult, DiscoveryPreset } from '@/utils/discovery/types';
import { fetcher } from '@/utils/fetcher';
import { routes } from '@/utils/routes';

type IssuerFilterProps = {
  filters: DiscoveryFilters;
  onSelectionComplete: (issuers?: string[]) => void;
  preset: DiscoveryPreset;
};

export const IssuerFilter = ({ filters, onSelectionComplete, preset }: IssuerFilterProps) => {
  const [selectedIssuers, setSelectedIssuers] = useState(filters.issuers);

  const { data: issuers, isLoading } = useQuery<DiscoveryIssuerResult[] | undefined, Error>({
    queryKey: ['discovery', 'token-issuers', preset],
    queryFn: () => fetcher(`${routes.api.DISCOVERY_TOKEN_ISSUERS}?preset=${preset}`),
  });

  const items: MultiselectItem[] = (issuers ?? []).map(({ name, logo }) => ({
    value: name,
    label: name,
    icon: issuerLogoOverride[name] ?? logo ?? 'unknown',
    iconRadius: issuerAvatarRadius,
    iconAlt: `${name} issuer`,
  }));

  const search = items.length > 8 ? { emptyLabel: 'No issuers found' } : undefined;

  // Sync local state with state.filters
  useEffect(() => setSelectedIssuers(filters.issuers), [filters.issuers]);

  if (!items.length && !isLoading) {
    return null;
  }

  return (
    <Multiselect
      items={items}
      value={isLoading ? undefined : selectedIssuers}
      onChange={(values) => setSelectedIssuers(values)}
      onDropdownClose={() => onSelectionComplete(selectedIssuers)}
      modal={true}
      triggerVariant="secondary"
      label={{ default: 'Issuer', entity: 'issuers', Icon: BuildingIcon }}
      multiselect={true}
      search={search}
      isLoading={isLoading}
    />
  );
};
